import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Search } from 'lucide-react';
import { Project } from '../types';
import { localized, parseDateStr, titleOf } from '../utils';
import { BACKEND_URL } from '../config';
import { authHeaders } from '../api';
import ProjectRow from '../components/ProjectRow';
import PageMeta from '../components/PageMeta';
import { RowsSkeleton } from '../components/Skeletons';

type SortOrder = 'newest' | 'oldest' | 'alpha_asc' | 'alpha_desc';

interface ProjectsProps { authenticated: boolean; }

const field = 'h-11 rounded-full bg-surface border border-line px-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/40';

export default function Projects({ authenticated }: ProjectsProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/projects`, { headers: authHeaders() })
      .then(r => r.json()).then(data => { setProjects(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const projectTypes = useMemo(
    () => Array.from(new Set(projects.map(p => localized(p.type, lang)).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [projects, lang]
  );

  const handleDelete = async (p: Project) => {
    if (!window.confirm(t('confirm_delete_project') as string)) return;
    const res = await fetch(`${BACKEND_URL}/api/projects/${p.strid}`, { method: 'DELETE', headers: authHeaders() }).catch(() => null);
    if (res?.ok) setProjects(prev => prev.filter(x => x.strid !== p.strid)); else toast.error(t('error_saving') as string);
  };

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = projects.filter(p =>
      (!typeFilter || localized(p.type, lang) === typeFilter) &&
      (!q || titleOf(p, lang).toLowerCase().includes(q) || localized(p.type, lang).toLowerCase().includes(q) ||
        (p.skills || []).some(s => localized(s.name, lang).toLowerCase().includes(q)))
    );
    const date = (d: string | null) => (d ? parseDateStr(d) : 0);
    return [...filtered].sort((a, b) => {
      if (sortOrder === 'newest') return date(b.start_date) - date(a.start_date);
      if (sortOrder === 'oldest') return date(a.start_date) - date(b.start_date);
      if (sortOrder === 'alpha_asc') return titleOf(a, lang).localeCompare(titleOf(b, lang));
      return titleOf(b, lang).localeCompare(titleOf(a, lang));
    });
  }, [projects, search, sortOrder, typeFilter, lang]);

  return (
    <>
      <PageMeta title={t('projects') as string} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <h1 className="font-display font-extrabold tracking-[-0.04em] text-5xl sm:text-[64px] leading-none">
            {t('projects')} <span className="text-primary-light">{projects.length}</span>
          </h1>
          {authenticated && (
            <Link to="/projects/new" className="h-11 px-5 inline-flex items-center gap-1.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors">
              <Plus size={16} />{t('add_project')}
            </Link>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <label className="relative flex-[1_1_240px]">
            <span className="sr-only">{t('search_projects')}</span>
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search_projects') as string} className={`${field} w-full pl-10`} />
          </label>
          <label className="flex-[0_1_220px]">
            <span className="sr-only">{t('type')}</span>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={`${field} w-full`}>
              <option value="">{t('all_types')}</option>
              {projectTypes.map(label => <option key={label} value={label}>{label}</option>)}
            </select>
          </label>
          <label className="flex-[0_1_180px]">
            <span className="sr-only">{t('sort')}</span>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value as SortOrder)} className={`${field} w-full`}>
              <option value="newest">{t('newest')}</option>
              <option value="oldest">{t('oldest')}</option>
              <option value="alpha_asc">A → Z</option>
              <option value="alpha_desc">Z → A</option>
            </select>
          </label>
        </div>

        {loading ? (
          <RowsSkeleton count={6} />
        ) : sorted.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">{t('no_projects_found')}</div>
        ) : (
          <div className="border-t-[1.5px] border-ink">
            {sorted.map(p => <ProjectRow key={p.strid} project={p} onDelete={authenticated ? handleDelete : undefined} />)}
          </div>
        )}
      </div>
    </>
  );
}
